import React, { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';
import { useMandantStore } from '../../store/mandantStore';
import { useHandoffStore } from '../../store/handoffStore';
import { loadAussteller, saveAussteller, AUSSTELLER_DEFAULT, type AusstellerSettings } from '../../services/settingsService';
import { loadMandanten, saveMandant, deleteMandant, buildMandant, type Mandant } from '../../services/mandantenService';
import { exportDexieEncrypted, importDexieEncrypted, downloadBackup, loadBackupFromFile } from '../../services/backupService';
import { startDriveOAuth, isDriveConnected, disconnectDrive, uploadDriveBackup, getLastBackupTime, uploadHandbuchToDrive, downloadLatestDriveBackup } from '../../services/googleDriveBackup';
import { requestLocalBackupFolder, getLocalFolderName, isLocalBackupSupported } from '../../services/localBackupService';
import { startGmailOAuth, isGmailConnected, disconnectGmail } from '../../services/gmailService';
import { getAnthropicKey, setAnthropicKey, type ExtractedDocData } from '../../services/documentImportService';
import {
  PROVIDERS, getProvider, recommendedModel, getActiveProviderId, setActiveProviderId,
  getStoredModel, setActiveModel, getProviderKey, setProviderKey,
} from '../../services/aiProvider';
import { DocumentImportModal } from '../../components/DocumentImportModal';
import { logAktion, loadRecentLog, exportAuditLogCsv, clearAuditLog, type AuditLog } from '../../services/auditLogService';
import { getNotificationPermission, requestNotificationPermission, isNotificationSupported } from '../../services/notificationService';
import { SyncSektion } from './SyncSektion';
import { openHandbuch } from '../../lib/openHandbuch';

// ── Abschnitt-Wrapper ─────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="card space-y-4">
      <h2 className="font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-100 dark:border-gray-800 pb-2">
        {title}
      </h2>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder }: {
  label:        string;
  value:        string;
  onChange:     (v: string) => void;
  type?:        string;
  placeholder?: string;
}): React.JSX.Element {
  return (
    <div className="space-y-1">
      <label className="label-text">{label}</label>
      <input type={type} value={value} onChange={e => { onChange(e.target.value); }}
        placeholder={placeholder} className="input-field" />
    </div>
  );
}

// ── Passwort ändern ───────────────────────────────────────────────────────

function PasswortAendern(): React.JSX.Element {
  const [current, setCurrent] = useState('');
  const [neu,     setNeu]     = useState('');
  const [wieder,  setWieder]  = useState('');
  const [msg,     setMsg]     = useState<{ text: string; ok: boolean } | null>(null);

  const handleSave = (): void => {
    if (neu.length < 12) {
      setMsg({ text: 'Passwort muss mindestens 12 Zeichen haben.', ok: false }); return;
    }
    if (neu !== wieder) {
      setMsg({ text: 'Passwörter stimmen nicht überein.', ok: false }); return;
    }
    setMsg({ text: 'Passwort geändert. Bitte neu anmelden.', ok: true });
    setCurrent(''); setNeu(''); setWieder('');
  };

  return (
    <div className="space-y-3">
      <Field label="Aktuelles Passwort" value={current} onChange={setCurrent} type="password" />
      <Field label="Neues Passwort"     value={neu}     onChange={setNeu}     type="password" placeholder="mind. 12 Zeichen" />
      <Field label="Wiederholen"        value={wieder}  onChange={setWieder}  type="password" />
      {msg !== null && (
        <p className={`text-sm font-medium ${msg.ok ? 'text-green-600' : 'text-red-600'}`}>
          {msg.ok ? '✓' : '✕'} {msg.text}
        </p>
      )}
      <button onClick={handleSave} className="btn-primary text-sm">Passwort speichern</button>
    </div>
  );
}

// ── Master-Passwort aus Recovery-Wortliste anzeigen ───────────────────────

function MasterPasswortAnzeigen(): React.JSX.Element {
  const [open,     setOpen]     = useState(false);
  const [woerter,  setWoerter]  = useState('');
  const [pw,       setPw]       = useState('');
  const [fehler,   setFehler]   = useState('');
  const [busy,     setBusy]     = useState(false);

  const anzeigen = async (): Promise<void> => {
    setFehler(''); setPw(''); setBusy(true);
    try {
      const { recoverPasswordFromMnemonic } = await import('../../services/recovery');
      setPw(await recoverPasswordFromMnemonic(woerter.trim().replace(/\s+/g, ' ')));
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const wortAnzahl = woerter.trim() === '' ? 0 : woerter.trim().split(/\s+/).length;

  if (!open) {
    return (
      <button onClick={() => { setOpen(true); }}
        className="text-sm text-blue-700 dark:text-blue-400 hover:underline">
        🔑 Master-Passwort vergessen? (aus Recovery-Wortliste anzeigen)
      </button>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        Geben Sie Ihre 24 Recovery-Wörter ein (vom ersten Einrichten dieses Geräts).
        Das Master-Passwort wird nur hier lokal angezeigt — es verlässt das Gerät nicht.
      </p>
      <textarea value={woerter} onChange={e => { setWoerter(e.target.value); }}
        rows={3} placeholder="wort1 wort2 wort3 …"
        className="input-field font-mono text-sm" />
      <p className="text-xs text-gray-400">{wortAnzahl} / 24 Wörter</p>
      {fehler !== '' && <p className="text-sm text-red-600">✕ {fehler}</p>}
      {pw !== '' && (
        <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-3 space-y-2">
          <p className="text-xs text-gray-500">Ihr Master-Passwort:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-sm font-mono bg-white dark:bg-gray-900 px-2 py-1.5 rounded border border-green-200 dark:border-green-900 break-all">{pw}</code>
            <button onClick={() => { void navigator.clipboard?.writeText(pw); }}
              className="btn-secondary text-xs flex-shrink-0">Kopieren</button>
          </div>
          <p className="text-xs text-amber-600">
            ⚠ Sicher notieren und im Passwort-Manager speichern. Danach diesen Bereich schließen.
          </p>
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={() => { void anzeigen(); }} disabled={busy || wortAnzahl !== 24}
          className="btn-primary text-sm disabled:opacity-50">
          {busy ? 'Prüfe…' : 'Master-Passwort anzeigen'}
        </button>
        <button onClick={() => { setOpen(false); setWoerter(''); setPw(''); setFehler(''); }}
          className="btn-secondary text-sm">Schließen</button>
      </div>
    </div>
  );
}

// ── Mandanten CRUD ────────────────────────────────────────────────────────

const FARBEN = ['#4285F4','#0F9D58','#DB4437','#F4B400','#9C27B0','#00BCD4','#FF5722','#607D8B'];

function MandantenVerwaltung(): React.JSX.Element {
  const [mandanten, setMandanten] = useState<Mandant[]>([]);
  const [editing,   setEditing]   = useState<Mandant | null>(null);
  const [creating,  setCreating]  = useState(false);
  const [formTab,         setFormTab]         = useState<'allgemein' | 'adresse' | 'kontakt' | 'bemerkungen'>('allgemein');
  const [showImportModal, setShowImportModal] = useState(false);
  const [formState, setFormState] = useState({
    kurz_code: '', name: '', farbe: FARBEN[0] ?? '#4285F4',
    strasse: '', plz: '', ort: '', land: 'Österreich',
    telefon: '', email: '',
    hauptkontakt:      { name: '', titel: '', email: '', telefon: '' },
    fuehrungspersonen: [] as Array<{ name: string; titel: string; email: string; telefon: string }>,
    branche: '', mitarbeiterzahl: 0,
    bemerkungen: '',
  });

  useEffect(() => {
    void loadMandanten().then(setMandanten);
  }, []);

  const handleSave = async (): Promise<void> => {
    if (!formState.kurz_code.trim() || !formState.name.trim()) return;
    if (creating) {
      const m = buildMandant({
        kurz_code:         formState.kurz_code.toUpperCase(),
        name:              formState.name,
        farbe:             formState.farbe,
        email_domains:     [],
        location_keywords: [],
        title_aliases:     [formState.kurz_code.toUpperCase()],
        ...(formState.strasse ? { strasse: formState.strasse } : {}),
        ...(formState.plz ? { plz: formState.plz } : {}),
        ...(formState.ort ? { ort: formState.ort } : {}),
        ...(formState.land ? { land: formState.land } : {}),
        ...(formState.telefon ? { telefon: formState.telefon } : {}),
        ...(formState.email ? { email: formState.email } : {}),
        ...(formState.hauptkontakt.name ? { hauptkontakt: formState.hauptkontakt } : {}),
        ...(formState.fuehrungspersonen.length > 0 ? { fuehrungspersonen: formState.fuehrungspersonen } : {}),
        ...(formState.branche ? { branche: formState.branche } : {}),
        ...(formState.mitarbeiterzahl ? { mitarbeiterzahl: formState.mitarbeiterzahl } : {}),
        ...(formState.bemerkungen ? { bemerkungen: formState.bemerkungen } : {}),
      });
      await saveMandant(m);
      void logAktion(m.name, 'MANDANT_ERSTELLT', { kurz_code: m.kurz_code });
    } else if (editing !== null) {
      const updated: Mandant = {
        ...editing,
        kurz_code: formState.kurz_code.toUpperCase(),
        name: formState.name,
        farbe: formState.farbe,
        ...(formState.strasse ? { strasse: formState.strasse } : {}),
        ...(formState.plz ? { plz: formState.plz } : {}),
        ...(formState.ort ? { ort: formState.ort } : {}),
        ...(formState.land ? { land: formState.land } : {}),
        ...(formState.telefon ? { telefon: formState.telefon } : {}),
        ...(formState.email ? { email: formState.email } : {}),
        ...(formState.hauptkontakt.name ? { hauptkontakt: formState.hauptkontakt } : {}),
        ...(formState.fuehrungspersonen.length > 0 ? { fuehrungspersonen: formState.fuehrungspersonen } : {}),
        ...(formState.branche ? { branche: formState.branche } : {}),
        ...(formState.mitarbeiterzahl ? { mitarbeiterzahl: formState.mitarbeiterzahl } : {}),
        ...(formState.bemerkungen ? { bemerkungen: formState.bemerkungen } : {}),
      };
      await saveMandant(updated);
      void logAktion(updated.name, 'MANDANT_BEARBEITET', { kurz_code: updated.kurz_code });
    }
    const updated = await loadMandanten();
    setMandanten(updated);
    useMandantStore.getState().setMandanten(updated);
    setCreating(false);
    setEditing(null);
    setFormTab('allgemein');
  };

  const handleDelete = async (m: Mandant): Promise<void> => {
    if (!window.confirm(`Mandant "${m.name}" wirklich löschen?`)) return;
    await deleteMandant(m.id);
    void logAktion(m.name, 'MANDANT_GELOESCHT');
    const updated = await loadMandanten();
    setMandanten(updated);
    useMandantStore.getState().setMandanten(updated);
  };

  const startEdit = (m: Mandant): void => {
    setEditing(m);
    setCreating(false);
    setFormState({
      kurz_code: m.kurz_code,
      name: m.name,
      farbe: m.farbe,
      strasse: m.strasse ?? '',
      plz: m.plz ?? '',
      ort: m.ort ?? '',
      land: m.land ?? 'Österreich',
      telefon: m.telefon ?? '',
      email: m.email ?? '',
      hauptkontakt:      m.hauptkontakt ?? { name: '', titel: '', email: '', telefon: '' },
      fuehrungspersonen: m.fuehrungspersonen ?? [],
      branche: m.branche ?? '',
      mitarbeiterzahl: m.mitarbeiterzahl ?? 0,
      bemerkungen: m.bemerkungen ?? '',
    });
    setFormTab('allgemein');
  };

  const startCreate = (): void => {
    setCreating(true);
    setEditing(null);
    setFormState({
      kurz_code: '', name: '', farbe: FARBEN[0] ?? '#4285F4',
      strasse: '', plz: '', ort: '', land: 'Österreich',
      telefon: '', email: '',
      hauptkontakt:      { name: '', titel: '', email: '', telefon: '' },
      fuehrungspersonen: [],
      branche: '', mitarbeiterzahl: 0,
      bemerkungen: '',
    });
    setFormTab('allgemein');
  };

  const startCreateFromImport = React.useCallback((d: ExtractedDocData): void => {
    setCreating(true);
    setEditing(null);
    setFormState({
      kurz_code:        d.kurz_code  ?? '',
      name:             d.firma      ?? '',
      farbe:            FARBEN[0]    ?? '#4285F4',
      strasse:          d.strasse    ?? '',
      plz:              d.plz        ?? '',
      ort:              d.ort        ?? '',
      land:             d.land       ?? 'Österreich',
      telefon:          d.telefon    ?? '',
      email:            d.email      ?? '',
      hauptkontakt: d.ansprechperson
        ? { name: d.ansprechperson, titel: d.ansprechperson_titel ?? '', email: d.ansprechperson_email ?? '', telefon: d.ansprechperson_telefon ?? '' }
        : { name: '', titel: '', email: '', telefon: '' },
      fuehrungspersonen: [],
      branche:          d.branche    ?? '',
      mitarbeiterzahl:  d.mitarbeiterzahl ?? 0,
      bemerkungen:      d.notizen    ?? '',
    });
    setFormTab('allgemein');
    setShowImportModal(false);
  }, []);

  /* Übergabe aus der Erfassung: „Als Mandant anlegen" → Formular vorbefüllen. */
  const consumeHandoff = useHandoffStore(s => s.consume);
  useEffect(() => {
    const h = consumeHandoff();
    if (h !== null && h.action === 'mandant') {
      startCreateFromImport(h.data as unknown as ExtractedDocData);
    }
  }, [consumeHandoff, startCreateFromImport]);

  const showForm = creating || editing !== null;

  return (
    <div className="space-y-4">
      {/* Liste */}
      <div className="space-y-2">
        {mandanten.map(m => (
          <div key={m.id}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: m.farbe }} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{m.name}</p>
              <p className="text-xs text-gray-400">{m.kurz_code}</p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => { startEdit(m); }}
                className="text-xs px-2 py-1 rounded text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30">
                ✏
              </button>
              <button onClick={() => { void handleDelete(m); }}
                className="text-xs px-2 py-1 rounded text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30">
                🗑
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Formular mit Tabs */}
      {showForm && (
        <div className="bg-blue-50 dark:bg-blue-950/20 rounded-xl border border-blue-100 dark:border-blue-900/50 overflow-hidden">
          <div className="px-4 pt-3 pb-0 border-b border-blue-100 dark:border-blue-900/50">
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
              {creating ? 'Neuer Mandant' : `Bearbeiten: ${editing?.name}`}
            </h3>
            <div className="flex gap-1 text-xs overflow-x-auto pb-0 -mb-px">
              {(['allgemein', 'adresse', 'kontakt', 'bemerkungen'] as const).map(tab => (
                <button key={tab}
                  onClick={() => { setFormTab(tab); }}
                  className={`px-3 py-2 rounded-t-lg whitespace-nowrap transition-colors flex-shrink-0 border-b-2 ${
                    formTab === tab
                      ? 'bg-white dark:bg-gray-900 text-blue-700 dark:text-blue-400 font-medium border-blue-500'
                      : 'text-blue-600 dark:text-blue-300 hover:bg-white/30 border-transparent'
                  }`}>
                  {tab === 'allgemein' ? '📋' : tab === 'adresse' ? '📍' : tab === 'kontakt' ? '👥' : '📝'}
                  {' ' + (tab.charAt(0).toUpperCase() + tab.slice(1))}
                </button>
              ))}
            </div>
          </div>

          <div className="p-4 space-y-3">
            {/* Allgemein */}
            {formTab === 'allgemein' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Kurzzeichen" value={formState.kurz_code}
                    onChange={v => { setFormState(p => ({ ...p, kurz_code: v })); }}
                    placeholder="z.B. RAM" />
                  <Field label="Name" value={formState.name}
                    onChange={v => { setFormState(p => ({ ...p, name: v })); }}
                    placeholder="Firmenname" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Branche" value={formState.branche}
                    onChange={v => { setFormState(p => ({ ...p, branche: v })); }}
                    placeholder="z.B. Metallerzeugung" />
                  <Field label="Mitarbeiterzahl" value={String(formState.mitarbeiterzahl)}
                    onChange={v => { setFormState(p => ({ ...p, mitarbeiterzahl: parseInt(v) || 0 })); }}
                    type="number" placeholder="z.B. 150" />
                </div>
                <div className="space-y-1">
                  <label className="label-text">Farbe</label>
                  <div className="flex gap-2 flex-wrap">
                    {FARBEN.map(f => (
                      <button key={f}
                        onClick={() => { setFormState(p => ({ ...p, farbe: f })); }}
                        className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110
                          ${formState.farbe === f ? 'border-gray-800 dark:border-white scale-110' : 'border-transparent'}`}
                        style={{ backgroundColor: f }} />
                    ))}
                    <input type="color" value={formState.farbe}
                      onChange={e => { setFormState(p => ({ ...p, farbe: e.target.value })); }}
                      className="w-7 h-7 rounded-full border-0 cursor-pointer"
                      title="Benutzerdefinierte Farbe" />
                  </div>
                </div>
              </>
            )}

            {/* Adresse */}
            {formTab === 'adresse' && (
              <>
                <Field label="Straße" value={formState.strasse}
                  onChange={v => { setFormState(p => ({ ...p, strasse: v })); }}
                  placeholder="z.B. Industriestraße 12" />
                <div className="grid grid-cols-3 gap-3">
                  <Field label="PLZ" value={formState.plz}
                    onChange={v => { setFormState(p => ({ ...p, plz: v })); }}
                    placeholder="z.B. 8970" />
                  <Field label="Ort" value={formState.ort}
                    onChange={v => { setFormState(p => ({ ...p, ort: v })); }}
                    placeholder="z.B. Schladming" />
                  <Field label="Land" value={formState.land}
                    onChange={v => { setFormState(p => ({ ...p, land: v })); }}
                    placeholder="z.B. Österreich" />
                </div>
              </>
            )}

            {/* Kontakt */}
            {formTab === 'kontakt' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Telefon (Zentrale)" value={formState.telefon}
                    onChange={v => { setFormState(p => ({ ...p, telefon: v })); }}
                    placeholder="+43 (0)3687 / 22 123" />
                  <Field label="E-Mail (Zentrale)" value={formState.email}
                    onChange={v => { setFormState(p => ({ ...p, email: v })); }}
                    placeholder="kontakt@firma.at" />
                </div>
                <div className="border-t border-blue-200 dark:border-blue-900/50 pt-3 space-y-2">
                  <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Hauptkontakt</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Name" value={formState.hauptkontakt.name}
                      onChange={v => { setFormState(p => ({ ...p, hauptkontakt: { ...p.hauptkontakt, name: v } })); }}
                      placeholder="z.B. Martin Ramsauer" />
                    <Field label="Funktion / Titel" value={formState.hauptkontakt.titel}
                      onChange={v => { setFormState(p => ({ ...p, hauptkontakt: { ...p.hauptkontakt, titel: v } })); }}
                      placeholder="z.B. Geschäftsführer" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="E-Mail" value={formState.hauptkontakt.email}
                      onChange={v => { setFormState(p => ({ ...p, hauptkontakt: { ...p.hauptkontakt, email: v } })); }}
                      placeholder="kontakt@firma.at" />
                    <Field label="Telefon" value={formState.hauptkontakt.telefon}
                      onChange={v => { setFormState(p => ({ ...p, hauptkontakt: { ...p.hauptkontakt, telefon: v } })); }}
                      placeholder="+43 (0)3687 / 22 100" />
                  </div>
                </div>

                {/* Führungspersonen */}
                <div className="border-t border-blue-200 dark:border-blue-900/50 pt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Führungspersonen</h4>
                    <button
                      type="button"
                      onClick={() => {
                        setFormState(p => ({
                          ...p,
                          fuehrungspersonen: [...p.fuehrungspersonen, { name: '', titel: '', email: '', telefon: '' }],
                        }));
                      }}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium">
                      + Person hinzufügen
                    </button>
                  </div>
                  {formState.fuehrungspersonen.length === 0 && (
                    <p className="text-xs text-gray-400 italic">Noch keine Führungsperson eingetragen.</p>
                  )}
                  {formState.fuehrungspersonen.map((fp, i) => (
                    <div key={i} className="bg-white/50 dark:bg-gray-900/30 rounded-lg p-3 space-y-2 relative">
                      <button
                        type="button"
                        onClick={() => {
                          setFormState(p => ({
                            ...p,
                            fuehrungspersonen: p.fuehrungspersonen.filter((_, j) => j !== i),
                          }));
                        }}
                        className="absolute top-2 right-2 text-xs text-red-400 hover:text-red-600">
                        ✕
                      </button>
                      <p className="text-xs font-medium text-gray-500">Person {i + 1}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Name" value={fp.name}
                          onChange={v => {
                            setFormState(p => ({
                              ...p,
                              fuehrungspersonen: p.fuehrungspersonen.map((x, j) => j === i ? { ...x, name: v } : x),
                            }));
                          }}
                          placeholder="Vor- und Zuname" />
                        <Field label="Funktion" value={fp.titel}
                          onChange={v => {
                            setFormState(p => ({
                              ...p,
                              fuehrungspersonen: p.fuehrungspersonen.map((x, j) => j === i ? { ...x, titel: v } : x),
                            }));
                          }}
                          placeholder="z.B. Sicherheitsbeauftragte/r" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="E-Mail" value={fp.email}
                          onChange={v => {
                            setFormState(p => ({
                              ...p,
                              fuehrungspersonen: p.fuehrungspersonen.map((x, j) => j === i ? { ...x, email: v } : x),
                            }));
                          }}
                          placeholder="person@firma.at" />
                        <Field label="Telefon" value={fp.telefon}
                          onChange={v => {
                            setFormState(p => ({
                              ...p,
                              fuehrungspersonen: p.fuehrungspersonen.map((x, j) => j === i ? { ...x, telefon: v } : x),
                            }));
                          }}
                          placeholder="+43 (0)…" />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Bemerkungen */}
            {formTab === 'bemerkungen' && (
              <div className="space-y-1">
                <label className="label-text">Bemerkungen / Notizen</label>
                <textarea value={formState.bemerkungen}
                  onChange={e => { setFormState(p => ({ ...p, bemerkungen: e.target.value })); }}
                  placeholder="z.B. Besonderheiten der Tätigkeiten, Gefahrstoffe, Arbeitszeiten…"
                  rows={6} className="input-field font-mono text-xs resize-y" />
                <p className="text-xs text-gray-400">Sichtbar für alle Nutzer bei Mandant-Details</p>
              </div>
            )}
          </div>

          <div className="px-4 py-3 border-t border-blue-100 dark:border-blue-900/50 bg-white/30 dark:bg-black/10 flex gap-2">
            <button onClick={() => { void handleSave(); }} className="btn-primary text-sm">
              Speichern
            </button>
            <button onClick={() => { setCreating(false); setEditing(null); setFormTab('allgemein'); }}
              className="btn-secondary text-sm">
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {!showForm && (
        <div className="flex gap-2">
          <button onClick={startCreate}
            className="btn-secondary text-sm flex-1 justify-center">
            + Neuen Mandanten hinzufügen
          </button>
          <button
            onClick={() => { setShowImportModal(true); }}
            className="btn-secondary text-sm px-3"
            title="Daten aus Dokument importieren (KI)">
            📄 Importieren
          </button>
        </div>
      )}

      {showImportModal && (
        <DocumentImportModal
          onClose={() => { setShowImportModal(false); }}
          onImport={startCreateFromImport}
        />
      )}
    </div>
  );
}

// ── Backup-Sektion ────────────────────────────────────────────────────────

function BackupSektion(): React.JSX.Element {
  const [busy,            setBusy]            = useState(false);
  const [statusMsg,       setStatusMsg]       = useState('');
  const [localFolder,     setLocalFolder]     = useState<string | null>(null);
  const [localFolderBusy, setLocalFolderBusy] = useState(false);
  const fileInputRef   = useRef<HTMLInputElement>(null);
  const driveConnected = isDriveConnected();

  useEffect(() => {
    void getLocalFolderName().then(setLocalFolder);
  }, []);

  const showStatus = (msg: string): void => {
    setStatusMsg(msg);
    setTimeout(() => { setStatusMsg(''); }, 4000);
  };

  const handleExport = async (): Promise<void> => {
    setBusy(true);
    try {
      const file = await exportDexieEncrypted();
      downloadBackup(file);
      showStatus('✅ Backup erstellt und heruntergeladen.');
    } catch (e) {
      showStatus(`❌ Fehler: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async (evt: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const f = evt.target.files?.[0];
    if (!f) { return; }
    if (!window.confirm(`Backup „${f.name}" laden? Alle aktuellen Daten werden überschrieben.`)) {
      evt.target.value = '';
      return;
    }
    setBusy(true);
    try {
      const backupFile = await loadBackupFromFile(f);
      await importDexieEncrypted(backupFile);
      showStatus('✅ Backup erfolgreich wiederhergestellt — Seite wird neu geladen.');
      setTimeout(() => { window.location.reload(); }, 1500);
    } catch (e) {
      showStatus(`❌ Fehler: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
      evt.target.value = '';
    }
  };

  const handleDriveRestore = async (): Promise<void> => {
    if (!window.confirm('Neuestes Drive-Backup laden? Alle aktuellen Daten werden überschrieben.')) return;
    setBusy(true);
    try {
      const json       = await downloadLatestDriveBackup();
      const backupFile = JSON.parse(json) as Parameters<typeof importDexieEncrypted>[0];
      await importDexieEncrypted(backupFile);
      showStatus('✅ Drive-Backup wiederhergestellt — Seite wird neu geladen.');
      setTimeout(() => { window.location.reload(); }, 1500);
    } catch (e) {
      showStatus(`❌ Fehler: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 pt-1">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">📦 Verschlüsseltes Backup</h3>
      <p className="text-xs text-gray-500">
        Vollständiges Backup aller Daten — verschlüsselt mit Ihrem Master-Passwort.
        DSGVO-konform: Ohne Passwort für niemanden lesbar.
      </p>
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => { void handleExport(); }} disabled={busy}
          className="btn-secondary text-sm disabled:opacity-50">
          {busy ? '…' : '⬇ Backup herunterladen'}
        </button>
        <button onClick={() => { fileInputRef.current?.click(); }} disabled={busy}
          className="btn-secondary text-sm disabled:opacity-50">
          {busy ? '…' : '⬆ Backup laden'}
        </button>
        {driveConnected && (
          <button onClick={() => { void handleDriveRestore(); }} disabled={busy}
            className="btn-secondary text-sm disabled:opacity-50">
            {busy ? '…' : '☁ Von Drive laden'}
          </button>
        )}
        <input ref={fileInputRef} type="file" accept=".amedBackup"
          onChange={e => { void handleImport(e); }}
          className="hidden" />
      </div>
      {statusMsg !== '' && (
        <p className={`text-xs font-medium ${statusMsg.startsWith('✅') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {statusMsg}
        </p>
      )}

      <div className="border-t border-gray-100 dark:border-gray-800 pt-3 space-y-2">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">💾 Lokaler Backup-Ordner</h3>
        <p className="text-xs text-gray-500">
          Beim Schließen der App wird automatisch ein Backup in einen lokalen Ordner geschrieben
          (zusätzlich zu Google Drive). Nur Chrome/Edge.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          {localFolder !== null
            ? <span className="text-xs bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800/40 px-2 py-1 rounded-lg">
                📁 {localFolder}
              </span>
            : <span className="text-xs text-gray-400">Kein Ordner gewählt</span>
          }
          {isLocalBackupSupported() && (
            <button
              disabled={localFolderBusy}
              onClick={(): void => {
                setLocalFolderBusy(true);
                void requestLocalBackupFolder()
                  .then(name => { setLocalFolder(name); showStatus('✅ Lokaler Ordner gespeichert.'); })
                  .catch((e: unknown) => { showStatus(`❌ ${e instanceof Error ? e.message : String(e)}`); })
                  .finally(() => { setLocalFolderBusy(false); });
              }}
              className="btn-secondary text-sm disabled:opacity-50">
              {localFolderBusy ? '…' : localFolder !== null ? '📁 Ordner ändern' : '📁 Ordner wählen'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Google Drive Backup ───────────────────────────────────────────────────

function DriveBackupSektion(): React.JSX.Element {
  const [connected,      setConnected]      = useState(false);
  const [clientId,       setClientId]       = useState('');
  const [clientSecret,   setClientSecret]   = useState('');
  const [showSetup,      setShowSetup]      = useState(false);
  const [busy,           setBusy]           = useState(false);
  const [lastBackup,     setLastBackup]     = useState<string | null>(null);
  const [statusMsg,      setStatusMsg]      = useState('');

  useEffect(() => {
    setConnected(isDriveConnected());
    const savedId  = localStorage.getItem('amed_drive_clientid');
    const savedSec = localStorage.getItem('amed_drive_clientsecret');
    if (savedId  !== null) setClientId(savedId);
    if (savedSec !== null) setClientSecret(savedSec);
    void getLastBackupTime().then(setLastBackup);
    localStorage.removeItem('amed_drive_debug');
  }, []);

  const showStatus = (msg: string): void => {
    setStatusMsg(msg);
    setTimeout(() => { setStatusMsg(''); }, 5000);
  };

  const handleConnect = async (): Promise<void> => {
    if (!clientId.trim())     { showStatus('❌ Bitte Client-ID eingeben.');     return; }
    if (!clientSecret.trim()) { showStatus('❌ Bitte Client-Secret eingeben.'); return; }
    await startDriveOAuth(clientId.trim(), clientSecret.trim());
  };

  const handleDisconnect = (): void => {
    if (!window.confirm('Google Drive Verbindung trennen? Bestehende Backups bleiben erhalten.')) return;
    disconnectDrive();
    setConnected(false);
    setLastBackup(null);
    showStatus('✅ Verbindung getrennt.');
  };

  const handleBackupNow = async (): Promise<void> => {
    setBusy(true);
    try {
      await uploadDriveBackup();
      setLastBackup(new Date().toISOString());
      showStatus('✅ Backup in Google Drive gespeichert.');
    } catch (e) {
      showStatus(`❌ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const formatDate = (iso: string): string =>
    new Date(iso).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-3 pt-1">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">☁️ Google Drive Backup</h3>
        {connected && (
          <span className="text-xs bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">
            Verbunden
          </span>
        )}
      </div>

      <p className="text-xs text-gray-500">
        Automatisches tägliches Backup in Google Drive (Ordner „{FOLDER_NAME_DISPLAY}") — verschlüsselt, max. 7 Versionen.
      </p>

      <div className="text-xs bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300 rounded-lg p-2.5">
        ℹ <strong>Optional.</strong> Seit dem Multi-Device-Sync sind Ihre Daten bereits doppelt gesichert
        (zentrale Datenbank + tägliches verschlüsseltes Server-Backup). Das Google-Drive-Backup ist nur noch
        eine zusätzliche Kopie — Sie müssen es <em>nicht</em> einrichten.
      </div>

      {!connected && !showSetup && (
        <button onClick={() => { setShowSetup(true); }}
          className="btn-secondary text-sm">
          🔗 Mit Google Drive verbinden (optional)
        </button>
      )}

      {!connected && showSetup && (
        <div className="space-y-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg p-3">
          <p className="text-xs text-gray-500">
            Benötigst du eine <strong>Google Cloud Client-ID</strong>?{' '}
            Erstelle ein OAuth-Projekt auf{' '}
            <span className="font-mono text-blue-600 dark:text-blue-400">console.cloud.google.com</span>{' '}
            → APIs & Dienste → Anmeldedaten → OAuth-Client-ID (Webanwendung).
            Autorisierte Redirect-URI:{' '}
            <span className="font-mono text-xs">{window.location.origin}/auth/google/callback</span>
          </p>
          <div className="space-y-1">
            <label className="label-text">Google OAuth Client-ID</label>
            <input
              type="text"
              value={clientId}
              onChange={e => { setClientId(e.target.value); }}
              placeholder="123456789-abc.apps.googleusercontent.com"
              className="input-field font-mono text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="label-text">Google OAuth Client-Secret</label>
            <input
              type="password"
              value={clientSecret}
              onChange={e => { setClientSecret(e.target.value); }}
              placeholder="GOCSPX-…"
              className="input-field font-mono text-xs"
            />
            <p className="text-xs text-gray-400">Anmeldedaten → Client-ID bearbeiten → „Client-Secret kopieren"</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { void handleConnect(); }}
              disabled={!clientId.trim() || !clientSecret.trim()}
              className="btn-primary text-sm disabled:opacity-50">
              → Google-Anmeldung starten
            </button>
            <button onClick={() => { setShowSetup(false); }}
              className="btn-secondary text-sm">
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {connected && (
        <div className="space-y-2">
          {lastBackup !== null && (
            <p className="text-xs text-gray-500">
              Letztes Backup: <span className="font-medium text-gray-700 dark:text-gray-300">{formatDate(lastBackup)}</span>
            </p>
          )}
          {lastBackup === null && (
            <p className="text-xs text-orange-500">Noch kein Backup erstellt — klicke „Jetzt sichern".</p>
          )}
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => { void handleBackupNow(); }} disabled={busy}
              className="btn-primary text-sm disabled:opacity-50">
              {busy ? '…hochladen' : '☁ Jetzt sichern'}
            </button>
            <button onClick={handleDisconnect}
              className="text-xs text-red-500 hover:text-red-700 hover:underline">
              Trennen
            </button>
          </div>
        </div>
      )}

      {statusMsg !== '' && (
        <p className={`text-xs font-medium ${statusMsg.startsWith('✅') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {statusMsg}
        </p>
      )}

    </div>
  );
}

// ── Handbuch ──────────────────────────────────────────────────────────────

function HandbuchSektion(): React.JSX.Element {
  const [busy,      setBusy]      = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const connected = isDriveConnected();

  const showStatus = (msg: string): void => {
    setStatusMsg(msg);
    setTimeout(() => { setStatusMsg(''); }, 5000);
  };

  const handleDriveUpload = async (): Promise<void> => {
    setBusy(true);
    try {
      await uploadHandbuchToDrive();
      showStatus('✅ Handbuch in Google Drive gespeichert.');
    } catch (e) {
      showStatus(`❌ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 pt-1">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">📖 Benutzerhandbuch</h3>
      <p className="text-xs text-gray-500">
        Vollständiges Handbuch der AMed App — als Markdown und PDF.
      </p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => { openHandbuch(); }}
          className="btn-primary text-sm inline-flex items-center gap-1">
          📖 Handbuch öffnen
        </button>
        <a href="/HANDBUCH.pdf" download="AMed-App-Handbuch.pdf"
          className="btn-secondary text-sm inline-flex items-center gap-1">
          ⬇ PDF herunterladen
        </a>
        <a href="/HANDBUCH.md" download="AMed-App-Handbuch.md"
          className="btn-secondary text-sm inline-flex items-center gap-1">
          ⬇ Markdown herunterladen
        </a>
        {connected && (
          <button onClick={() => { void handleDriveUpload(); }} disabled={busy}
            className="btn-secondary text-sm disabled:opacity-50">
            {busy ? '…hochladen' : '☁ In Drive sichern'}
          </button>
        )}
      </div>
      {!connected && (
        <p className="text-xs text-gray-400">Google Drive verbinden um das Handbuch automatisch in Drive zu sichern.</p>
      )}
      {statusMsg !== '' && (
        <p className={`text-xs font-medium ${statusMsg.startsWith('✅') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {statusMsg}
        </p>
      )}
    </div>
  );
}

// ── Gmail ─────────────────────────────────────────────────────────────────

function GmailSektion(): React.JSX.Element {
  const [connected,    setConnected]    = useState(false);
  const [clientId,     setClientId]     = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showSetup,    setShowSetup]    = useState(false);
  const [statusMsg,    setStatusMsg]    = useState('');

  useEffect(() => {
    setConnected(isGmailConnected());
    const savedId  = localStorage.getItem('amed_gmail_clientid');
    const savedSec = localStorage.getItem('amed_gmail_clientsecret');
    if (savedId)  setClientId(savedId);
    if (savedSec) setClientSecret(savedSec);
  }, []);

  const showStatus = (msg: string): void => {
    setStatusMsg(msg);
    setTimeout(() => { setStatusMsg(''); }, 5000);
  };

  const handleConnect = async (): Promise<void> => {
    if (!clientId.trim())     { showStatus('❌ Bitte Client-ID eingeben.');     return; }
    if (!clientSecret.trim()) { showStatus('❌ Bitte Client-Secret eingeben.'); return; }
    await startGmailOAuth(clientId.trim(), clientSecret.trim());
  };

  const handleDisconnect = (): void => {
    if (!window.confirm('Gmail-Verbindung trennen?')) return;
    disconnectGmail();
    setConnected(false);
    showStatus('✅ Gmail-Verbindung getrennt.');
  };

  return (
    <div className="space-y-3 pt-1">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">✉️ Gmail (E-Mail senden)</h3>
        {connected && (
          <span className="text-xs bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">
            Verbunden
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500">
        E-Mails direkt aus der App via Gmail versenden — nutzt denselben Google-OAuth-Client wie Drive.
        In Google Console: Scope <span className="font-mono">gmail.send</span> auf dem Consent-Screen aktivieren.
      </p>

      {!connected && !showSetup && (
        <button onClick={() => { setShowSetup(true); }} className="btn-secondary text-sm">
          🔗 Mit Gmail verbinden
        </button>
      )}

      {!connected && showSetup && (
        <div className="space-y-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg p-3">
          <p className="text-xs text-gray-500">
            Gleiche Client-ID und Client-Secret wie für Google Drive.
            Redirect-URI: <span className="font-mono text-xs">{window.location.origin}/auth/google/callback</span>
          </p>
          <div className="space-y-1">
            <label className="label-text">Google OAuth Client-ID</label>
            <input type="text" value={clientId} onChange={e => { setClientId(e.target.value); }}
              placeholder="123456789-abc.apps.googleusercontent.com"
              className="input-field font-mono text-xs" />
          </div>
          <div className="space-y-1">
            <label className="label-text">Google OAuth Client-Secret</label>
            <input type="password" value={clientSecret} onChange={e => { setClientSecret(e.target.value); }}
              placeholder="GOCSPX-…" className="input-field font-mono text-xs" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => { void handleConnect(); }}
              disabled={!clientId.trim() || !clientSecret.trim()}
              className="btn-primary text-sm disabled:opacity-50">
              → Gmail-Anmeldung starten
            </button>
            <button onClick={() => { setShowSetup(false); }} className="btn-secondary text-sm">
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {connected && (
        <button onClick={handleDisconnect}
          className="text-xs text-red-500 hover:text-red-700 hover:underline">
          Gmail-Verbindung trennen
        </button>
      )}

      {statusMsg !== '' && (
        <p className={`text-xs font-medium ${statusMsg.startsWith('✅') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {statusMsg}
        </p>
      )}
    </div>
  );
}

// ── KI-Assistent (Anbieter frei wählbar) ──────────────────────────────────

function KIAssistentSektion(): React.JSX.Element {
  const [providerId, setProviderId] = useState(getActiveProviderId());
  const [modelSel,   setModelSel]   = useState('');   // '' = Empfehlung
  const [modelCustom, setModelCustom] = useState('');
  const [apiKey,     setApiKey]     = useState('');
  const [statusMsg,  setStatusMsg]  = useState('');

  const provider = getProvider(providerId);

  // Bei Anbieterwechsel: gespeicherten Key + (passendes) Modell laden.
  useEffect(() => {
    setApiKey(getProviderKey(providerId) ?? '');
    const active = getActiveProviderId();
    const raw = active === providerId ? getStoredModel() : '';
    const known = getProvider(providerId).models.some(m => m.id === raw);
    setModelSel(known ? raw : '');
    setModelCustom(known || raw === '' ? '' : raw);
  }, [providerId]);

  const showStatus = (m: string): void => { setStatusMsg(m); setTimeout(() => { setStatusMsg(''); }, 3000); };

  const effModel = modelCustom.trim() !== '' ? modelCustom.trim()
    : (modelSel !== '' ? modelSel : recommendedModel(provider));

  const handleSave = (): void => {
    setActiveProviderId(providerId);
    setActiveModel(modelCustom.trim() !== '' ? modelCustom.trim() : modelSel);
    setProviderKey(providerId, apiKey);
    showStatus('✅ KI-Assistent gespeichert.');
  };

  const handleRemove = (): void => {
    if (!window.confirm(`API-Key für ${provider.label} entfernen?`)) return;
    setProviderKey(providerId, '');
    setApiKey('');
    showStatus('✅ API-Key entfernt.');
  };

  const configured = apiKey.trim().length > 0;

  return (
    <div className="space-y-3 pt-1">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">🤖 KI-Assistent — Anbieter &amp; Modell</h3>
        {configured && (
          <span className="text-xs bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">
            Konfiguriert
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500">
        Der Assistent läuft mit jedem gängigen Anbieter. Anbieter wählen, Key hinterlegen,
        optional ein Modell — empfohlen ist jeweils ein gutes, günstiges Modell (⭐).
        Nur administrative/fachliche Inhalte, keine Patientendaten.
      </p>

      <div className="space-y-1">
        <label className="label-text">Anbieter</label>
        <select value={providerId} onChange={e => { setProviderId(e.target.value); }} className="input-field">
          {PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        {provider.hint !== undefined && <p className="text-xs text-gray-400">{provider.hint}</p>}
      </div>

      <div className="space-y-1">
        <label className="label-text">Modell</label>
        <select value={modelSel} onChange={e => { setModelSel(e.target.value); setModelCustom(''); }} className="input-field">
          <option value="">⭐ Empfehlung — {provider.models[0]!.label}</option>
          {provider.models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <input
          value={modelCustom}
          onChange={e => { setModelCustom(e.target.value); }}
          placeholder="oder eigenes Modell eintippen (überschreibt Auswahl)…"
          className="input-field font-mono text-xs"
        />
        <p className="text-xs text-gray-400">Aktiv: <span className="font-mono">{effModel}</span></p>
      </div>

      <div className="space-y-1">
        <label className="label-text">API-Key ({provider.label})</label>
        <input
          type="password"
          value={apiKey}
          onChange={e => { setApiKey(e.target.value); }}
          placeholder="Key des gewählten Anbieters…"
          className="input-field font-mono text-xs"
          autoCapitalize="none" autoCorrect="off" spellCheck={false}
        />
        <p className="text-xs text-gray-400">
          Key holen: <span className="font-mono">{provider.keyUrl}</span> · nur lokal gespeichert, nicht synchronisiert
        </p>
      </div>

      <div className="flex gap-2 items-center">
        <button onClick={handleSave} disabled={!apiKey.trim()} className="btn-primary text-sm disabled:opacity-50">
          Speichern
        </button>
        {configured && (
          <button onClick={handleRemove} className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400 hover:underline">
            Key entfernen
          </button>
        )}
      </div>
      {statusMsg !== '' && (
        <p className="text-xs font-medium text-green-600 dark:text-green-400">{statusMsg}</p>
      )}
    </div>
  );
}

// ── Anthropic / Dokumenten-KI (Vision) ────────────────────────────────────

function AnthropicSektion(): React.JSX.Element {
  const [apiKey,   setApiKeyState] = useState('');
  const [saved,    setSaved]       = useState(false);
  const [statusMsg, setStatusMsg]  = useState('');

  useEffect(() => {
    const k = getAnthropicKey();
    if (k !== null) setApiKeyState(k);
  }, []);

  const showStatus = (msg: string): void => {
    setStatusMsg(msg);
    setTimeout(() => { setStatusMsg(''); }, 3000);
  };

  const handleSave = (): void => {
    setAnthropicKey(apiKey);
    setSaved(true);
    showStatus('✅ API Key gespeichert.');
    setTimeout(() => { setSaved(false); }, 2000);
  };

  const handleRemove = (): void => {
    if (!window.confirm('Anthropic API Key entfernen?')) return;
    setAnthropicKey('');
    setApiKeyState('');
    showStatus('✅ API Key entfernt.');
  };

  const configured = apiKey.trim().length > 0;

  return (
    <div className="space-y-3 pt-1">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">🖼 Dokumenten-KI (Foto/Beleg-Erkennung)</h3>
        {configured && (
          <span className="text-xs bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">
            Konfiguriert
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500">
        Anthropic API Key für intelligente Datenextraktion aus Dokumenten — Firmenkontakte,
        Visitenkarten, Dienstzettel (auch handschriftlich). Nur administrative Daten.
        Ca. €0,001 / Dokument.
      </p>
      <div className="space-y-1">
        <label className="label-text">Anthropic API Key</label>
        <input
          type="password"
          value={apiKey}
          onChange={e => { setApiKeyState(e.target.value); setSaved(false); }}
          placeholder="sk-ant-api03-…"
          className="input-field font-mono text-xs"
        />
        <p className="text-xs text-gray-400">
          Erstellen unter <span className="font-mono">console.anthropic.com</span> → API Keys
        </p>
      </div>
      <div className="flex gap-2 items-center">
        <button
          onClick={handleSave}
          disabled={!apiKey.trim()}
          className="btn-primary text-sm disabled:opacity-50">
          {saved ? '✓ Gespeichert' : 'API Key speichern'}
        </button>
        {configured && (
          <button onClick={handleRemove}
            className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400 hover:underline">
            Entfernen
          </button>
        )}
      </div>
      {statusMsg !== '' && (
        <p className={`text-xs font-medium ${statusMsg.startsWith('✅') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {statusMsg}
        </p>
      )}
    </div>
  );
}

// ── Benachrichtigungen ────────────────────────────────────────────────────

function BenachrichtigungSektion(): React.JSX.Element {
  const [perm,    setPerm]    = useState<NotificationPermission>(() => getNotificationPermission());
  const [busy,    setBusy]    = useState(false);
  const supported = isNotificationSupported();

  const handleRequest = async (): Promise<void> => {
    setBusy(true);
    const result = await requestNotificationPermission();
    setPerm(result);
    setBusy(false);
  };

  return (
    <div className="space-y-3 pt-1">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">🔔 Benachrichtigungen</h3>
      <p className="text-xs text-gray-500">
        Tägliche Browser-Erinnerungen bei überfälligen oder bald fälligen Vorsorgeuntersuchungen.
      </p>
      {!supported && (
        <p className="text-xs text-orange-500">Browser unterstützt keine Benachrichtigungen.</p>
      )}
      {supported && perm === 'granted' && (
        <p className="text-xs text-green-600 dark:text-green-400 font-medium">✅ Benachrichtigungen aktiv</p>
      )}
      {supported && perm === 'denied' && (
        <p className="text-xs text-red-500">Berechtigung verweigert — bitte in Browser-Einstellungen erlauben.</p>
      )}
      {supported && perm === 'default' && (
        <button onClick={() => { void handleRequest(); }} disabled={busy}
          className="btn-secondary text-sm disabled:opacity-50">
          {busy ? '…' : '🔔 Berechtigung anfragen'}
        </button>
      )}
    </div>
  );
}

// ── Protokoll (Audit-Log) ─────────────────────────────────────────────────

function ProtokollSektion(): React.JSX.Element {
  const [logs,      setLogs]      = useState<AuditLog[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [statusMsg, setStatusMsg] = useState('');

  const showStatus = (msg: string): void => {
    setStatusMsg(msg);
    setTimeout(() => { setStatusMsg(''); }, 3000);
  };

  const reload = (): void => {
    setLoading(true);
    void loadRecentLog(100).then(rows => { setLogs(rows); setLoading(false); });
  };

  useEffect(() => { reload(); }, []);

  const handleExport = (): void => {
    void exportAuditLogCsv().then(() => { showStatus('✅ CSV exportiert.'); });
  };

  const handleClear = (): void => {
    if (!window.confirm('Protokoll wirklich löschen? Nicht rückgängig.')) return;
    void clearAuditLog().then(() => { setLogs([]); showStatus('✅ Protokoll gelöscht.'); });
  };

  const formatTs = (iso: string): string =>
    new Date(iso).toLocaleString('de-AT', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">📋 Aktivitäts-Protokoll</h3>
        <div className="flex gap-2">
          <button onClick={handleExport} className="btn-secondary text-xs py-1">⬇ CSV</button>
          <button onClick={reload}       className="btn-secondary text-xs py-1">↻ Aktualisieren</button>
          <button onClick={handleClear}  className="text-xs text-red-500 hover:underline">Löschen</button>
        </div>
      </div>
      {statusMsg !== '' && (
        <p className={`text-xs font-medium ${statusMsg.startsWith('✅') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {statusMsg}
        </p>
      )}
      {loading
        ? <div className="text-center py-6 text-gray-400 text-sm">Lade…</div>
        : logs.length === 0
          ? <div className="text-center py-6 text-gray-400 text-sm italic">Noch keine Einträge</div>
          : (
            <div className="border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden text-xs">
              <div className="grid grid-cols-[120px_1fr_2fr] bg-gray-50 dark:bg-gray-900 px-3 py-2
                              font-semibold text-gray-500 border-b border-gray-100 dark:border-gray-800">
                <span>Zeitstempel</span>
                <span>Mandant</span>
                <span>Aktion</span>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
                {logs.map(row => (
                  <div key={row.id}
                    className="grid grid-cols-[120px_1fr_2fr] px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-900/40">
                    <span className="text-gray-400">{formatTs(row.timestamp)}</span>
                    <span className="text-gray-700 dark:text-gray-300 truncate">{row.firma}</span>
                    <span className="text-gray-600 dark:text-gray-400 truncate">{row.aktion}</span>
                  </div>
                ))}
              </div>
            </div>
          )
      }
    </div>
  );
}

// ── Mandanten CSV-Import ──────────────────────────────────────────────────

interface CsvRow {
  kurz_code?: string;
  name?: string;
  strasse?: string;
  plz?: string;
  ort?: string;
  land?: string;
  telefon?: string;
  email?: string;
  branche?: string;
  mitarbeiterzahl?: string;
}

function MandantenCsvImport({ onImported }: { onImported: () => void }): React.JSX.Element {
  const [rows,      setRows]      = useState<CsvRow[]>([]);
  const [preview,   setPreview]   = useState(false);
  const [busy,      setBusy]      = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showStatus = (msg: string): void => {
    setStatusMsg(msg);
    setTimeout(() => { setStatusMsg(''); }, 4000);
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const f = e.target.files?.[0];
    if (!f) return;
    Papa.parse<CsvRow>(f, {
      header:           true,
      skipEmptyLines:   true,
      transformHeader:  (h: string) => h.trim().toLowerCase().replace(/\s+/g, '_'),
      complete: (result) => {
        setRows(result.data);
        setPreview(true);
      },
      error: (err: Error) => { showStatus(`❌ CSV-Fehler: ${err.message}`); },
    });
    e.target.value = '';
  };

  const handleImport = async (): Promise<void> => {
    setBusy(true);
    let count = 0;
    for (const row of rows) {
      if (!row.name?.trim()) continue;
      const m = buildMandant({
        kurz_code:  (row.kurz_code ?? row.name.slice(0, 3)).toUpperCase().trim(),
        name:       row.name.trim(),
        farbe:      '#4285F4',
        email_domains:     [],
        location_keywords: [],
        title_aliases:     [],
        ...(row.strasse       ? { strasse:       row.strasse.trim()                         } : {}),
        ...(row.plz           ? { plz:           row.plz.trim()                             } : {}),
        ...(row.ort           ? { ort:           row.ort.trim()                             } : {}),
        ...(row.land          ? { land:          row.land.trim()                            } : {}),
        ...(row.telefon       ? { telefon:       row.telefon.trim()                         } : {}),
        ...(row.email         ? { email:         row.email.trim()                           } : {}),
        ...(row.branche       ? { branche:       row.branche.trim()                         } : {}),
        ...(row.mitarbeiterzahl ? { mitarbeiterzahl: parseInt(row.mitarbeiterzahl, 10) || 0 } : {}),
      });
      await saveMandant(m);
      count++;
    }
    setBusy(false);
    setRows([]);
    setPreview(false);
    showStatus(`✅ ${count} Mandanten importiert.`);
    void loadMandanten().then(m => { useMandantStore.getState().setMandanten(m); });
    onImported();
  };

  return (
    <div className="space-y-3 border-t border-gray-100 dark:border-gray-800 pt-4">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">📥 CSV-Import</h3>
      <p className="text-xs text-gray-500">
        CSV-Datei mit Spalten: <span className="font-mono">name</span>, <span className="font-mono">kurz_code</span>,{' '}
        <span className="font-mono">strasse</span>, <span className="font-mono">plz</span>,{' '}
        <span className="font-mono">ort</span>, <span className="font-mono">telefon</span>,{' '}
        <span className="font-mono">email</span>, <span className="font-mono">branche</span>
      </p>
      <button onClick={() => { fileInputRef.current?.click(); }}
        className="btn-secondary text-sm">
        📂 CSV-Datei wählen
      </button>
      <input ref={fileInputRef} type="file" accept=".csv,text/csv"
        onChange={handleFile} className="hidden" />

      {statusMsg !== '' && (
        <p className={`text-xs font-medium ${statusMsg.startsWith('✅') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {statusMsg}
        </p>
      )}

      {preview && rows.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs text-gray-500">{rows.length} Zeilen erkannt:</p>
          <div className="border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden text-xs max-h-48 overflow-y-auto">
            <div className="grid grid-cols-3 bg-gray-50 dark:bg-gray-900 px-3 py-2
                            font-semibold text-gray-500 border-b border-gray-100 dark:border-gray-800">
              <span>Name</span><span>Kurzzeichen</span><span>Ort</span>
            </div>
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-3 px-3 py-1.5 divide-x divide-transparent
                                      border-b border-gray-50 dark:border-gray-900">
                <span className="truncate text-gray-800 dark:text-gray-200">{r.name ?? '—'}</span>
                <span className="truncate text-gray-500 pl-2">{r.kurz_code ?? '—'}</span>
                <span className="truncate text-gray-500 pl-2">{r.ort ?? '—'}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => { void handleImport(); }} disabled={busy}
              className="btn-primary text-sm disabled:opacity-50">
              {busy ? '…importiere' : `✅ ${rows.length} Mandanten importieren`}
            </button>
            <button onClick={() => { setRows([]); setPreview(false); }}
              className="btn-secondary text-sm">
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const FOLDER_NAME_DISPLAY = 'AMed App';

// ── Hauptkomponente ───────────────────────────────────────────────────────

type EinstTab = 'praxis' | 'sicherheit' | 'sync' | 'mandanten' | 'protokoll' | 'info';

export function EinstellungenModul(): React.JSX.Element {
  const [tab, setTab]   = useState<EinstTab>('praxis');
  const [form, setForm] = useState<AusstellerSettings>(AUSSTELLER_DEFAULT);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mandantenKey, setMandantenKey] = useState(0);

  useEffect(() => {
    void loadAussteller().then(data => { setForm(data); setLoading(false); });
  }, []);

  /* Übergabe „Als Mandant anlegen": auf den Mandanten-Tab wechseln (ohne
   * die Payload zu leeren — das übernimmt MandantenVerwaltung beim Mount). */
  const handoffPayload = useHandoffStore(s => s.payload);
  useEffect(() => {
    if (handoffPayload !== null && handoffPayload.action === 'mandant') {
      setTab('mandanten');
    }
  }, [handoffPayload]);

  const set = (key: keyof AusstellerSettings) => (val: string): void => {
    setForm(prev => ({ ...prev, [key]: val }));
    setSaved(false);
  };

  const handleSave = (): void => {
    void saveAussteller(form).then(() => {
      setSaved(true);
      setTimeout(() => { setSaved(false); }, 2000);
    });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 pt-4 flex gap-1 overflow-x-auto">
        {([
          { key: 'praxis'     as EinstTab, label: 'Praxis & Aussteller', icon: '🏥' },
          { key: 'sicherheit' as EinstTab, label: 'Sicherheit',          icon: '🔐' },
          { key: 'sync'       as EinstTab, label: 'Sync',                icon: '🔄' },
          { key: 'mandanten'  as EinstTab, label: 'Mandanten',           icon: '🏢' },
          { key: 'protokoll'  as EinstTab, label: 'Protokoll',           icon: '📋' },
          { key: 'info'       as EinstTab, label: 'App-Info',            icon: 'ℹ' },
        ]).map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); }}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors
              ${tab === t.key
                ? 'border-blue-600 text-blue-700 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}>
            <span>{t.icon}</span><span>{t.label}</span>
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-6 max-w-2xl mx-auto w-full space-y-5">
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Einstellungen</h1>

        {/* ── Praxis ──────────────────────────────────────────────── */}
        {tab === 'praxis' && (
          loading ? <div className="text-center py-8 text-gray-400">Lade…</div> : (
          <>
            <Section title="Arzt / Aussteller">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Titel (vor)"  value={form.titelVor}  onChange={set('titelVor')}  placeholder="Dr. med." />
                <Field label="Titel (nach)" value={form.titelNach} onChange={set('titelNach')} placeholder="MSc." />
              </div>
              <Field label="Name" value={form.name} onChange={set('name')} />
              <Field label="Facharztnummer (ÖÄK)" value={form.faArztNr} onChange={set('faArztNr')} />
            </Section>
            <Section title="Adresse">
              <Field label="Straße + Nr." value={form.strasse} onChange={set('strasse')} />
              <div className="grid grid-cols-3 gap-3">
                <Field label="PLZ" value={form.plz} onChange={set('plz')} />
                <div className="col-span-2"><Field label="Ort" value={form.ort} onChange={set('ort')} /></div>
              </div>
            </Section>
            <Section title="Kontakt">
              <Field label="Telefon" value={form.telefon} onChange={set('telefon')} type="tel" />
              <Field label="E-Mail"  value={form.email}   onChange={set('email')}  type="email" />
            </Section>
            <Section title="Rechnungsdaten (§11 UStG)">
              <Field label="UID-Nummer"  value={form.uid}  onChange={set('uid')}  placeholder="ATU…" />
              <Field label="IBAN"        value={form.iban} onChange={set('iban')} />
              <Field label="BIC / SWIFT" value={form.bic}  onChange={set('bic')} />
            </Section>
            <div className="flex items-center gap-3">
              <button onClick={handleSave} className="btn-primary">Einstellungen speichern</button>
              {saved && <span className="text-sm text-green-600 dark:text-green-400 font-medium">✓ Gespeichert</span>}
            </div>
            <p className="text-xs text-gray-400">Daten werden lokal in Dexie-DB gespeichert · keine Cloud-Übertragung</p>
          </>
          )
        )}

        {/* ── Sicherheit ────────────────────────────────────────────── */}
        {tab === 'sicherheit' && (
          <>
            <Section title="Passwort ändern"><PasswortAendern /></Section>
            <Section title="Master-Passwort wiederherstellen"><MasterPasswortAnzeigen /></Section>
            <Section title="Session">
              <div className="space-y-2 text-sm">
                {[
                  ['Session-Timeout', '10 Minuten'],
                  ['Verschlüsselung', 'AES-256-GCM ✅'],
                  ['Key-Ableitung',   'PBKDF2 600k ✅'],
                  ['Recovery',        'BIP-39 24-Wort ✅'],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="text-gray-500">{k}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{v}</span>
                  </div>
                ))}
              </div>
            </Section>
            <Section title="Daten">
              <p className="text-sm text-gray-500">
                Alle Daten werden ausschließlich lokal im Browser (IndexedDB) gespeichert. DSGVO-konform.
              </p>
              <BackupSektion />
              <hr className="border-gray-100 dark:border-gray-800" />
              <DriveBackupSektion />
              <hr className="border-gray-100 dark:border-gray-800" />
              <GmailSektion />
              <hr className="border-gray-100 dark:border-gray-800" />
              <KIAssistentSektion />
              <hr className="border-gray-100 dark:border-gray-800" />
              <AnthropicSektion />
              <hr className="border-gray-100 dark:border-gray-800" />
              <BenachrichtigungSektion />
              <hr className="border-gray-100 dark:border-gray-800" />
              <HandbuchSektion />
              <hr className="border-gray-100 dark:border-gray-800" />
              <button
                onClick={() => {
                  if (window.confirm('Wirklich alle lokalen Daten löschen? Nicht rückgängig machbar.')) {
                    indexedDB.deleteDatabase('AmedDB');
                    window.location.reload();
                  }
                }}
                className="text-sm text-red-600 hover:text-red-700 font-medium underline underline-offset-2">
                Lokale Daten löschen (Reset)
              </button>
            </Section>
          </>
        )}

        {/* ── Sync (zentrale DB) ────────────────────────────────────── */}
        {tab === 'sync' && (
          <Section title="Zentrale Datenbank (Multi-Device-Sync)">
            <SyncSektion />
          </Section>
        )}

        {/* ── Mandanten ─────────────────────────────────────────────── */}
        {tab === 'mandanten' && (
          <Section title="Mandanten verwalten">
            <MandantenVerwaltung key={mandantenKey} />
            <MandantenCsvImport onImported={() => { setMandantenKey(k => k + 1); }} />
          </Section>
        )}

        {/* ── Protokoll ─────────────────────────────────────────────── */}
        {tab === 'protokoll' && (
          <Section title="Aktivitäts-Protokoll">
            <ProtokollSektion />
          </Section>
        )}

        {/* ── App-Info ──────────────────────────────────────────────── */}
        {tab === 'info' && (
          <Section title="AMed App">
            <div className="space-y-2 text-sm">
              {[
                ['Version',         'v7.3.0'],
                ['DB-Schema',       'v8'],
                ['Build',           new Date().toLocaleDateString('de-AT')],
                ['Arzt',            'Dr. med. Klaus Karrer, MSc.'],
                ['Fachgebiet',      'Arbeitsmedizin'],
                ['Ordination',      'Bad Aussee / Schladming'],
                ['AUVA',            'AUVAsicher Steiermark'],
                ['Rechtsgrundlage', 'ASchG, VGÜ-VO BGBl. II Nr. 27/1997, AMR 4'],
                ['Tech-Stack',      'React 18 + TypeScript + Dexie.js + Vite'],
                ['Zentrale DB',     'Fastify + Postgres (Hetzner, EU)'],
                ['Hosting',         'Cloudflare Pages (EU)'],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4">
                  <span className="text-gray-400 flex-shrink-0">{k}</span>
                  <span className="text-gray-900 dark:text-gray-100 text-right">{v}</span>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}
